import { Category } from "@/types/general";
import {
    selectCategoryByCode,
    useCreateCategoryMutation,
    useGetCategoriesQuery,
    useUpdateCategoryMutation,
} from "@redux/features/categories/categoriesApi";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";

import BaseButton from "@reusables/BaseButton";
import BaseInput from "@reusables/BaseInput";
import BaseModal from "@reusables/Modals/BaseModal";
import { toast } from "react-toastify";

import CategoryCommandDropdown from "@/components/ready/CategoryCommandDropdown";
import PowerofficeIntegrationSection from "@components/Dashboard/pages/SettingsPage/General/Categories/modals/MutationModal/PowerofficeIntegrationSection";
import { removeEmpty, toastError } from "@helpers/utils";
import { zodResolver } from "@hookform/resolvers/zod";
import { isSystemIntegratedSelector } from "@redux/features/auth/authSlice";
import { integrationsApi } from "@redux/features/integrations/integrationsApi";
import { powerofficeApi } from "@redux/features/integrations/powerofficeApi";
import { useAppDispatch, useAppSelector } from "@redux/hooks";
import { globalStore } from "@redux/store";
import BaseCheckbox from "@reusables/BaseCheckbox";
import { Controller, useForm } from "react-hook-form";
import { z } from "zod";
import TwentyFourSevenOfficeIntegrationSection from "./TwentyFourSevenOfficeIntegrationSection";

// Props control the modal lifecycle and supply the category context.
interface MutationModalProperties {
    isOpen: boolean;
    onClose: () => void;
    category?: Category.Root;
    parentCategoryId?: number | null;
}

// Form schema defines fields, validation, and integration sub-objects.
const formScheme = z.object({
    parent_category_id: z.number().nullish(),
    code: z
        .object({
            value: z.string().optional(),
            checkable: z.boolean().optional(),
        })
        .refine(
            (code) => {
                return code.checkable
                    ? selectCategoryByCode(globalStore.getState(), code.value) === undefined
                    : true;
            },
            {
                params: {
                    i18n: "general.validation.custom.code.unique",
                },
                path: ["value"],
            }
        ),
    name: z.string(),
    is_service: z.boolean(),
    integrations: z.object({
        poweroffice: z
            .object({
                sales_account: z
                    .object({
                        id: z.number(),
                        code: z.number(),
                        name: z.string(),
                    })
                    .nullish(),
            })
            .nullish(),
        twenty_four_seven_office: z
            .object({
                sales_account: z
                    .object({
                        id: z.number(),
                        name: z.string(),
                        number: z.number(),
                    })
                    .nullish(),
            })
            .nullish(),
    }),
});

export type CategoryMutationFormTyping = z.infer<typeof formScheme>;

// Utility to find a node anywhere in the category tree.
function findCategoryById(categories: Category.Root[], id: number): Category.Root | undefined {
    for (const category of categories) {
        if (category.id === id) {
            return category;
        }
        const childMatch = findCategoryById(category.children ?? [], id);
        if (childMatch) {
            return childMatch;
        }
    }
    return undefined;
}

// Utility to collect all descendant ids for "self + subtree" exclusion.
function collectDescendantIds(category: Category.Root, ids: Set<number>): void {
    ids.add(category.id);
    category.children?.forEach((child) => collectDescendantIds(child, ids));
}

// Utility to prune nodes from a tree while preserving structure.
function filterCategories(categories: Category.Root[], excludedIds: Set<number>): Category.Root[] {
    return categories
        .filter((category) => !excludedIds.has(category.id))
        .map((category) => ({
            ...category,
            children: filterCategories(category.children ?? [], excludedIds),
        }));
}

export default function MutationModal(props: MutationModalProperties) {
    const { t } = useTranslation("", { keyPrefix: "settings.general.categories.modals.mutation" });

    // App state used for integration-specific sections and lookups.
    const dispatch = useAppDispatch();
    const isPowerOfficeIntegrated = useAppSelector(isSystemIntegratedSelector("poweroffice"));
    const isTwentyFourSevenOfficeIntegrated = useAppSelector(
        isSystemIntegratedSelector("twenty_four_seven_office")
    );

    // Source of truth for tree data used by parent dropdown and filters.
    const { data: categories = [] } = useGetCategoriesQuery();

    // Getting RTK Query hooks for saving data
    const [createCategory, { isLoading: isCreationLoading }] = useCreateCategoryMutation();
    const [updateCategory, { isLoading: isUpdateLoading }] = useUpdateCategoryMutation();

    // react-hook-form controls the modal fields and validation.
    const { control, handleSubmit, setValue, reset } = useForm<CategoryMutationFormTyping>({
        resolver: zodResolver(formScheme),
        defaultValues: {
            parent_category_id: null,
            code: {
                checkable: true,
            },
            is_service: false,
            integrations: {},
        },
    });

    // Initialize or refresh form state when modal opens or category context changes.
    useEffect(() => {
        reset();

        // Parent comes from the row that opened the modal (or null for root creation).
        setValue("parent_category_id", props.parentCategoryId ?? null);

        if (props.category) {
            // Edit mode: seed basic fields from the existing category.
            setValue("code.value", props.category.code);
            setValue("code.checkable", false);
            setValue("name", props.category.name);
            setValue("is_service", props.category.is_service);

            // Edit mode: hydrate integrations from external sources by id.
            const categoryPoweroffice = props.category.integrations.poweroffice;
            if (categoryPoweroffice) {
                dispatch(
                    powerofficeApi.endpoints.getPOGOSalesAccounts.initiate(undefined, {
                        subscribe: false,
                    })
                )
                    .unwrap()
                    .then((sas) => {
                        const salesAccount = sas.find(
                            (sa) => sa.id == categoryPoweroffice.sales_account_id
                        );
                        if (salesAccount) {
                            setValue("integrations.poweroffice.sales_account", salesAccount);
                        }
                    })
                    .catch(console.error);
            }

            const category24SO = props.category.integrations.twenty_four_seven_office;
            if (category24SO) {
                dispatch(
                    integrationsApi.endpoints.getTwentyFourSevenOfficeSalesAccounts.initiate(
                        undefined,
                        {
                            subscribe: false,
                        }
                    )
                )
                    .unwrap()
                    .then((sas) => {
                        const salesAccount = sas.find(
                            (sa) => sa.id == category24SO.sales_account_id
                        );
                        if (salesAccount) {
                            setValue(
                                "integrations.twenty_four_seven_office.sales_account",
                                salesAccount
                            );
                        }
                    })
                    .catch(console.error);
            }
        }
    }, [props.category, props.parentCategoryId, reset, props.isOpen, setValue]);

    // Compute ids that must not be selectable as parent (self + descendants).
    const excludedParentIds = React.useMemo(() => {
        if (!props.category) {
            return new Set<number>();
        }

        const currentCategory = findCategoryById(categories, props.category.id);
        const ids = new Set<number>();
        if (currentCategory) {
            collectDescendantIds(currentCategory, ids);
        }
        return ids;
    }, [categories, props.category]);

    // Filter out invalid parent candidates while preserving the tree structure.
    const filteredParentOptions = React.useMemo(
        () =>
            excludedParentIds.size > 0
                ? filterCategories(categories, excludedParentIds)
                : categories,
        [categories, excludedParentIds]
    );

    // Submit handler builds integrations payload and switches create vs update.
    const onSubmit = handleSubmit((data) => {
        // Convert integration selectors to the API shape (ids only).
        const integrationsData = {
            integrations: {
                ...(data.integrations.poweroffice?.sales_account && {
                    poweroffice: {
                        sales_account_id: data.integrations.poweroffice.sales_account.id,
                    },
                }),
                ...(data.integrations.twenty_four_seven_office?.sales_account && {
                    twenty_four_seven_office: {
                        sales_account_id:
                            data.integrations.twenty_four_seven_office.sales_account.id,
                    },
                }),
            },
        };

        if (props.category) {
            // Update existing category.
            updateCategory({
                id: props.category.id,
                name: data.name,
                is_service: data.is_service,
                parent_category_id: data.parent_category_id ?? null,
                ...integrationsData,
            })
                .unwrap()
                .then(() => {
                    toast.success(t("responses.update.success"));
                })
                .catch(toastError)
                .finally(() => {
                    props.onClose();
                });
        } else {
            // Create new category (omit empty fields and nulls).
            createCategory(
                removeEmpty({
                    name: data.name,
                    code: data.code.value,
                    is_service: data.is_service,
                    parent_category_id: data.parent_category_id ?? undefined,
                    ...integrationsData,
                })
            )
                .unwrap()
                .then(() => {
                    toast.success(t("responses.creation.success"));
                })
                .catch(toastError)
                .finally(() => {
                    props.onClose();
                });
        }
    }, console.error);

    return (
        <BaseModal
            isOpen={props.isOpen}
            onClose={props.onClose}
            width={562}
            padding="56px"
            useCloseIcon
            isLoading={isCreationLoading || isUpdateLoading}
        >
            <form className="space-y-[32px]" onSubmit={onSubmit}>
                {/* Header reflects edit vs create mode. */}
                <div className="text-xl font-semibold text-center text-accent">
                    {props.category ? props.category.name : t("creationHeading")}
                </div>

                {/* Parent selection uses the command dropdown with filtered options. */}
                <Controller
                    name={"parent_category_id"}
                    control={control}
                    render={({ field, fieldState }) => (
                        <CategoryCommandDropdown
                            options={filteredParentOptions}
                            onSelect={(e) => {
                                field.onChange(
                                    e.current?.item ? (e.current.item as Category.Root).id : null
                                );
                            }}
                            value={field.value ?? null}
                            error={fieldState.error}
                        />
                    )}
                />

                {/* Core identity fields: code (create only) and name. */}
                <div className="flex flex-row items-center space-x-2">
                    <div className="w-1/5">
                        <Controller
                            name={"code.value"}
                            control={control}
                            render={({ field, fieldState }) => (
                                <BaseInput
                                    {...field}
                                    error={fieldState.error}
                                    label={t("fields.number.label")}
                                    disabled={props.category !== undefined}
                                />
                            )}
                        />
                    </div>

                    <Controller
                        name={"name"}
                        control={control}
                        render={({ field, fieldState }) => (
                            <BaseInput
                                {...field}
                                error={fieldState.error}
                                label={t("fields.name.label")}
                            />
                        )}
                    />
                </div>

                {/* Service flag influences badges and downstream behavior. */}
                <Controller
                    name={"is_service"}
                    control={control}
                    render={({ field }) => (
                        <BaseCheckbox {...field} label={t("fields.isService.label")} />
                    )}
                />

                {/* Integration sections are conditionally rendered per system. */}
                {isPowerOfficeIntegrated && <PowerofficeIntegrationSection control={control} />}

                {isTwentyFourSevenOfficeIntegrated && (
                    <TwentyFourSevenOfficeIntegrationSection control={control} />
                )}

                {/* Primary action submits create or update. */}
                <div>
                    <BaseButton
                        buttonWidth="100%"
                        text={props.category ? t("buttons.save") : t("buttons.create")}
                        size="md"
                        type="submit"
                    />
                </div>
            </form>
        </BaseModal>
    );
}
